import { useEffect, useRef } from 'react';
import { useAuth } from 'context/AuthContext';

/**
 * Merge-on-login listener for the intro visit counter (STORY-09.3.03).
 *
 * Mounted once inside the providers' scope; renders nothing. Watches the
 * AuthContext currentUser ref and, on the transition from
 * anonymous → logged-in, calls `mergeIntroState(uid)` once to reconcile
 * the localStorage cache with the Firestore profile (Math.max wins; one-
 * way upgrade so we never reduce a higher remote count).
 *
 * Why a separate component instead of inside AuthContext (decision #1 from
 * the pre-flight plan):
 *   - Keeps AuthContext untouched (existing rule: no scope-creep on shared
 *     context files unless necessary).
 *   - The merge call dynamically imports `services/introProfile` so the
 *     Firestore code is only fetched after a sign-in (admins, anonymous
 *     visits, and visits with no auth session never pay the bundle cost).
 *
 * Distinguishes "logged in this session" from "session already had auth
 * on page load": the merge only fires when prev === null and next !== null
 * within the same component lifetime. A user who arrives at the app
 * already signed in (prev starts as null but gets the user on first render)
 * also gets a merge — which is the correct behaviour for cross-device
 * reconciliation on each fresh tab load.
 */
const IntroLoginMerge = () => {
  const { currentUser } = useAuth();
  const prevUidRef = useRef(undefined);  // sentinel: first render hasn't latched yet

  useEffect(() => {
    const prevUid = prevUidRef.current;
    const nextUid = currentUser && currentUser.uid;

    // Skip the no-change cases.
    if (prevUid === nextUid) {
      prevUidRef.current = nextUid;
      return;
    }

    // Only act on transition INTO an authenticated state. Logout (next null)
    // doesn't trigger anything — localStorage stays intact for the next
    // anonymous-session use.
    if (nextUid) {
      // Dynamic import keeps the Firestore introProfile module out of the
      // initial bundle for visitors who never sign in.
      import('services/introProfile')
        .then(({ mergeIntroState }) => mergeIntroState(nextUid))
        .catch(() => {
          // Silent — the service itself reports to Sentry. Nothing here
          // should throw to the React render tree.
        });
    }

    prevUidRef.current = nextUid;
  }, [currentUser]);

  return null;
};

export default IntroLoginMerge;
